import { useEffect, useMemo, useRef, useState } from "react";
import {
  markBlockingPopupClosed,
  markBlockingPopupOpen,
} from "../../utils/blockingPopups";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { IonButton, IonModal, useIonToast } from "@ionic/react";
import { claimDailyBonus } from "../../redux/actions/coinsActions";
import dsCoinImage from "../../assets/images/ds-coin.png";
import "./CoinTransactionToast.css";
import { readAuth } from "../../services/authStorage";
import { isOnboardingIncomplete } from "../../services/onboardingProfile";
import { CONFIRMATION_TOAST_MS } from "../../constants/toasts";

const confettiPieces = Array.from({ length: 18 }, (_, index) => index);

const DAILY_BONUS_SEEN_KEY = "ds_daily_bonus_seen";

/**
 * Today's date in Rome, as YYYY-MM-DD.
 *
 * Must be Rome and not the device's zone: the backend decides what "today"
 * means with now()->toDateString() under APP_TIMEZONE=Europe/Rome. A member in
 * another timezone comparing local dates would roll the popup over at the wrong
 * hour and either show it twice on one Italian day or skip it entirely.
 */
const romeToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome" }).format(
    new Date(),
  );

const dailyBonusSeenToday = () => {
  try {
    return localStorage.getItem(DAILY_BONUS_SEEN_KEY) === romeToday();
  } catch {
    // Private mode / storage disabled: better to show it again than to crash.
    return false;
  }
};

const markDailyBonusSeen = () => {
  try {
    localStorage.setItem(DAILY_BONUS_SEEN_KEY, romeToday());
  } catch {
    /* nothing we can do, and nothing worth breaking the popup over */
  }
};

const getActionName = (action) => {
  if (!action) return "azione";
  if (typeof action === "string") return action;
  return action.name || "azione";
};

const getActionCode = (action) => {
  if (!action || typeof action === "string") return "";
  return action.code || "";
};

const CoinTransactionToast = () => {
  const [presentToast] = useIonToast();
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const lastTransaction = useSelector((state) => state.user?.coins?.lastTransaction);
  const prevTransactionIdRef = useRef(null);
  const [rewardTransaction, setRewardTransaction] = useState(null);

  const isAuthenticated = useSelector(
    (state) => !!state.user?.adoc_t && !!state.user?.user,
  );
  const claimedRef = useRef(false);

  // The gender field name varies across older accounts (gender/sex/sesso), so
  // check all three — same lookup as the home banner. Unknown gender keeps the
  // female phrasing, which is the app's existing default.
  const subscriber = useSelector((state) => state.user?.master_club_subscriber);
  const isMale = ["m", "male", "maschio"].includes(
    String(subscriber?.gender || subscriber?.sex || subscriber?.sesso || "")
      .toLowerCase()
      .trim(),
  );

  // `/login/:token` and `/impersonate/:token` are token HAND-OFF routes: the app
  // mounts first and those pages overwrite localStorage.adoc_t afterwards. Until
  // that has happened the app is still holding the PREVIOUS session's token.
  //
  // Claiming during that window sends the wrong person's credentials. It is what
  // put a member's own daily-bonus popup on screen over an impersonated account:
  // the operator was signed in as another member, the app booted on
  // /impersonate/:token and claimed with HER token, then the hand-off swapped the
  // profile underneath the popup. Nothing was mis-credited — the coins were
  // genuinely hers — but it read as the impersonated member being paid.
  //
  // The backend cannot catch this: at that instant the token really is a valid
  // member token, not an impersonation one.
  const handingOverToken = /^\/(login|impersonate)\//.test(pathname);

  // Onboarding first, celebration after (client, 17/09): a new member saw the
  // "+150 Monete DS" welcome popup on top of onboarding step 1. The claim below
  // is what makes the backend nominate it, so it waits until onboarding is done;
  // and a popup nominated while the member is still on those screens (the
  // profile refresh after the last step) is held until the next page — the Free
  // Programme screen, or Home.
  const me = useSelector((state) => state.user?.user);
  const onboardingDone =
    !!me &&
    !!subscriber &&
    !isOnboardingIncomplete({ user: me, master_club_subscriber: subscriber });
  const inOnboardingFlow =
    /^\/(impostazioni-iniziali|onboarding-complete)(\/|$)/.test(pathname);
  const pendingWelcomeRef = useRef(null);

  // Ask for the daily bonus once per app start. This is what actually credits
  // it on an app open, so it runs even when the popup has already been seen
  // today — only the popup is once-a-day, never the coins.
  //
  // Not during a CRM support session: an operator looking at the account is not
  // the member returning, so it must neither earn them coins nor burn the popup
  // they have not seen. The backend refuses this independently (it reads the
  // impersonation token, which cannot be faked from here) — skipping the call is
  // just the cheaper half of the same rule.
  //
  // `handingOverToken` is a dependency on purpose. Once the hand-off finishes and
  // the route becomes /home this re-runs: an impersonated session is by then
  // flagged and skips for good, while a magic-link login claims normally — with
  // the new token rather than the old one.
  useEffect(() => {
    if (!isAuthenticated || claimedRef.current) return;
    if (handingOverToken) return;
    if (readAuth("adoc_i") === "true") return;
    // Also credits the day's login coins: during onboarding they only wait.
    if (!onboardingDone) return;
    claimedRef.current = true;
    dispatch(claimDailyBonus());
  }, [dispatch, isAuthenticated, handingOverToken, onboardingDone]);

  // Closes the remaining window: a hand-off performed in the SAME tab (the CRM
  // button opens a new one, but /impersonate/:token can be pasted anywhere) can
  // begin while a popup is already up. That popup belongs to whoever was signed
  // in a second ago, so take it down rather than let it hang over the account
  // support has just opened.
  useEffect(() => {
    const onImpersonationChanged = () => {
      if (readAuth("adoc_i") === "true") setRewardTransaction(null);
    };
    window.addEventListener("impersonation-changed", onImpersonationChanged);
    return () =>
      window.removeEventListener("impersonation-changed", onImpersonationChanged);
  }, []);

  const transactionKey = useMemo(() => {
    if (!lastTransaction) return null;
    const actionName = getActionName(lastTransaction.action);
    const actionCode = getActionCode(lastTransaction.action);
    return (
      lastTransaction.id ||
      `${lastTransaction.source || "coin"}:${lastTransaction.balance_after}:${
        lastTransaction.points
      }:${actionCode || actionName}`
    );
  }, [lastTransaction]);

  useEffect(() => {
    if (
      !lastTransaction ||
      !transactionKey ||
      lastTransaction.points <= 0 ||
      transactionKey === prevTransactionIdRef.current
    ) {
      return;
    }

    const isPostReward = lastTransaction.source === "addPost";
    const isDailyBonus = lastTransaction.source === "dailyLogin";
    // The registration bonus gets the celebration, not the small green toast the
    // `else` below shows for ordinary earnings — being welcomed with "Hai
    // guadagnato 150 monete per …" is exactly the anticlimax this popup exists
    // to avoid. The server has already decided this app start is the one to show
    // it, once ever, so there is nothing to check here.
    const isWelcomeBonus = lastTransaction.source === "welcomeBonus";
    if (isWelcomeBonus && inOnboardingFlow) {
      pendingWelcomeRef.current = lastTransaction;
    } else if (isPostReward || isWelcomeBonus) {
      setRewardTransaction(lastTransaction);
    } else if (isDailyBonus) {
      // Reaching here already means the BACKEND nominated this app start to
      // show the popup (show_popup), which it grants once per member per Italian
      // day — so a member on the phone and the web app still sees it once.
      //
      // This local marker is only a backstop for the one case the server cannot
      // cover: if Redis were flushed mid-day the server could nominate a second
      // time, and this stops the same device showing it twice. It can never
      // wrongly suppress, because it is written only when the popup actually
      // goes on screen — so "set to today" always means "this device already
      // showed it today". Written on display rather than on dismissal, so
      // force-quitting mid-popup cannot make it reappear next launch.
      if (!dailyBonusSeenToday()) {
        markDailyBonusSeen();
        setRewardTransaction(lastTransaction);
      }
    } else {
      const actionName = getActionName(lastTransaction.action);
      presentToast({
        message: `Hai guadagnato ${lastTransaction.points} monete per ${actionName}!`,
        duration: CONFIRMATION_TOAST_MS,
        color: "success",
        position: "bottom",
      });
    }

    prevTransactionIdRef.current = transactionKey;
  }, [lastTransaction, presentToast, transactionKey, inOnboardingFlow]);

  // The held welcome popup, once the member is past the onboarding screens.
  useEffect(() => {
    if (inOnboardingFlow || !pendingWelcomeRef.current) return;
    setRewardTransaction(pendingWelcomeRef.current);
    pendingWelcomeRef.current = null;
  }, [inOnboardingFlow]);

  const showingDailyBonus = rewardTransaction?.source === "dailyLogin";
  const showingWelcomeBonus = rewardTransaction?.source === "welcomeBonus";

  const popupOpen = !!rewardTransaction;

  // Claim the screen while a reward card is up. The dashboard's first-access
  // video checks this and waits rather than opening over the top of it — the
  // member was reading a congratulation and the video was pulling it away
  // mid-sentence. Registered as an effect so an unmount (navigation, logout)
  // releases the claim and the video is never blocked for good.
  useEffect(() => {
    if (!popupOpen) return undefined;
    markBlockingPopupOpen();
    return () => markBlockingPopupClosed();
  }, [popupOpen]);

  // The popup must NOT auto-dismiss: the user has to tap "Continua" so we can
  // then take them to the post they just published in the feed. For the daily
  // bonus the button does nothing but close — the coins were credited before
  // the popup ever appeared, so there is nothing to confirm and nowhere to go.
  const handleContinue = () => {
    const groupId = rewardTransaction?.groupId;
    const postGroupId = rewardTransaction?.postGroupId;
    setRewardTransaction(null);
    if (showingDailyBonus || showingWelcomeBonus) return;
    if (groupId && postGroupId) {
      history.push(
        `/gruppi/${groupId}?focusPost=${postGroupId}&focusAnchor=1`,
      );
    }
  };

  return (
    <IonModal
      isOpen={popupOpen}
      onDidDismiss={() => setRewardTransaction(null)}
      cssClass="coin-reward-modal"
      showBackdrop
      backdropDismiss={false}
    >
      <div className="coin-reward-card">
        <div className="coin-reward-confetti" aria-hidden="true">
          {confettiPieces.map((piece) => (
            <span key={piece} style={{ "--i": piece }} />
          ))}
        </div>

        <div className="coin-reward-coin-wrap">
          <img src={dsCoinImage} alt="" className="coin-reward-coin" />
        </div>

        <div className="coin-reward-kicker">
          {showingWelcomeBonus
            ? "🎉 Benvenut@ in Dieta Social!"
            : showingDailyBonus
              ? "🎉 Bonus giornaliero!"
              : "Post pubblicato!"}
        </div>
        <div className="coin-reward-points">
          +{rewardTransaction?.points || 0}
        </div>
        <div className="coin-reward-title">Monete DS guadagnate</div>
        {rewardTransaction?.isVideoProcessing && (
          <p className="coin-reward-video-note">
            Il post verrà pubblicato al termine dell&apos;elaborazione del
            video.
          </p>
        )}
        <p className="coin-reward-copy">
          {showingWelcomeBonus
            ? `Hai ricevuto +${rewardTransaction?.points || 0} Monete DS come bonus di benvenuto. Sono già sul tuo saldo.`
            : showingDailyBonus
              ? `Hai ricevuto +${rewardTransaction?.points || 0} Monete DS per essere ${
                  isMale ? "tornato" : "tornata"
                } oggi su Dieta Social.`
              : "Le monete sono state accreditate automaticamente sul tuo saldo."}
        </p>

        {/* What yesterday actually earned them — shown only when there is
            something to report, so nobody is congratulated on zero. */}
        {showingDailyBonus && rewardTransaction?.yesterdayPoints > 0 ? (
          <div className="coin-reward-yesterday">
            <p className="coin-reward-yesterday-title">
              🎉 Ieri hai guadagnato {rewardTransaction.yesterdayPoints} Monete
              DS!
            </p>
            <p className="coin-reward-yesterday-copy">
              Post, reazioni, attività e partecipazione alla Community ti fanno
              accumulare Monete DS.
            </p>
          </div>
        ) : null}

        <IonButton
          className="coin-reward-button"
          expand="block"
          onClick={handleContinue}
        >
          {showingDailyBonus || showingWelcomeBonus ? "Grazie!" : "Continua"}
        </IonButton>
      </div>
    </IonModal>
  );
};

export default CoinTransactionToast;
